import { unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { html } from "lit/static-html.js";
import EkoElement from "@/utilities/element";
import style from "./confirmation-dialog.css?inline";

@customElement("eko-confirmation-dialog")
class ConfirmationDialog extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private dialogRef: Ref<HTMLDialogElement> = createRef();

  private handleCancel = () => {
    this.settleConfirmation(false);
    this.dialogRef.value?.close("cancel");
  };

  // explicitly don't close the dialog here to allow the consumer to handle
  // any async work before closing the dialog
  private handleSubmit = (e: Event) => {
    e.preventDefault();
    this.loading = true; // show the button loading spinner
    this.settleConfirmation(true);
  };

  @state() private loading = false;

  // eslint-disable-next-line no-unused-vars
  private resolveConfirmation?: (value: boolean) => void;

  @property({ reflect: true }) cancel?: string = "Cancel";

  close = () => {
    this.dialogRef.value?.close();
  };

  confirmationPromise = () =>
    new Promise<boolean>((resolve) => {
      this.resolveConfirmation = resolve;
      // user has clicked outside the dialog or pressed ESC key
      this.dialogRef.value?.addEventListener("close", () => this.settleConfirmation(false), {
        once: true,
      });
    });

  @property({ reflect: true }) heading?: string = "Are you sure?";
  @property({ reflect: true }) message?: string = "Are you sure?";

  show = () => {
    this.dialogRef.value?.showModal();
  };

  @property({ reflect: true }) submit?: string = "Confirm";
  @property({ reflect: true }) theme?: "default" | "danger" = "default";

  private settleConfirmation(value: boolean) {
    this.resolveConfirmation?.(value);
    this.resolveConfirmation = undefined;
  }

  render() {
    return html`
      <dialog
        closedby="any"
        role="alertdialog"
        ${ref(this.dialogRef)}
        @close=${() => {
          this.loading = false;
        }}>
        <div class="confirmation-dialog__body">
          <div class="confirmation-dialog__content">
            <eko-subheading size="lg">${this.heading}</eko-subheading>
            <eko-paragraph theme="weak">${this.message}</eko-paragraph>
          </div>

          <eko-button-group variant="equal" reverse>
            <eko-button
              ?loading=${this.loading}
              variant="primary"
              theme=${this.theme}
              data-confirmation-dialog-target="submitButton"
              slotted>
              <form slot="button" @submit=${this.handleSubmit}>
                <button type="submit" tabindex="-1" style="cursor: pointer;">${this.submit}</button>
              </form>
            </eko-button>

            <eko-button slotted>
              <button slot="button" type="button" @click=${this.handleCancel} tabindex="-1">
                ${this.cancel}
              </button>
            </eko-button>
          </eko-button-group>
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-confirmation-dialog": ConfirmationDialog;
  }
}

export default ConfirmationDialog;
